import {
  AuthRequestOptions,
  BackstageIdentityResponse,
  OAuthScope,
  ProfileInfo,
  SessionState,
} from '@backstage/core-plugin-api';
import { Observable } from '@backstage/types';
import { GithubSession, MockGithubAuthApi } from './runwayAuthApiRefs';
import { EmuGithubAuthApi } from '../refs/emuAuth.ref';

const DEFAULT_EMU_GITHUB_SCOPES: OAuthScope = ['repo', 'read:user', 'read:org'];

export class EmuGithubAuthAdapter implements EmuGithubAuthApi {
  private loginHint: string | undefined = undefined;

  constructor(private readonly githubAuth: EmuGithubAuthApi) {}

  setLoginHint(hint: string): void {
    this.loginHint = hint;
  }

  async getAccessToken(
    scope: OAuthScope = DEFAULT_EMU_GITHUB_SCOPES,
    options: AuthRequestOptions = {},
  ): Promise<string> {
    // Plant the cookie before falling back to the popup if a hint is set
    if (this.loginHint) {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `__emu_login_hint=${encodeURIComponent(this.loginHint)}; path=/api/auth; max-age=30; SameSite=Strict${secure}`;
    }
    return this.githubAuth.getAccessToken(scope, options);
  }

  signOut(): Promise<void> {
    return this.githubAuth.signOut();
  }

  signIn(): Promise<void> {
    return this.githubAuth.signIn();
  }

  sessionState$(): Observable<SessionState> {
    return this.githubAuth.sessionState$();
  }

  getProfile(options?: AuthRequestOptions): Promise<ProfileInfo | undefined> {
    return this.githubAuth.getProfile(options);
  }

  getBackstageIdentity(options?: AuthRequestOptions): Promise<BackstageIdentityResponse | undefined> {
    return this.githubAuth.getBackstageIdentity(options);
  }
}

export class MockEmuGithubAuthAdapter implements EmuGithubAuthApi {
  private loginHint: string | undefined = undefined;
  private readonly mockGithubAuth: MockGithubAuthApi;

  constructor(storageKey = 'emuGithubSession') {
    this.mockGithubAuth = new MockGithubAuthApi(storageKey);
  }

  setLoginHint(hint: string): void {
    this.loginHint = hint;
  }

  hasSession(): boolean {
    return this.mockGithubAuth.hasSession();
  }

  saveSession(session: GithubSession | undefined): void {
    this.mockGithubAuth.saveSession(session);
  }

  async getAccessToken(
    scope: OAuthScope = DEFAULT_EMU_GITHUB_SCOPES,
    options: AuthRequestOptions = {},
  ): Promise<string> {
    // Plant the cookie before redirecting to login if no session is present.
    if (this.loginHint) {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `__emu_login_hint=${encodeURIComponent(this.loginHint)}; path=/api/auth; max-age=30; SameSite=Strict${secure}`;
    }
    return this.mockGithubAuth.getAccessToken(scope, options);
  }

  signOut(): Promise<void> {
    return this.mockGithubAuth.signOut();
  }

  signIn(): Promise<void> {
    return this.mockGithubAuth.signIn();
  }

  sessionState$(): Observable<SessionState> {
    return this.mockGithubAuth.sessionState$();
  }

  getProfile(options?: AuthRequestOptions): Promise<ProfileInfo | undefined> {
    return this.mockGithubAuth.getProfile(options);
  }

  getBackstageIdentity(options?: AuthRequestOptions): Promise<BackstageIdentityResponse | undefined> {
    return this.mockGithubAuth.getBackstageIdentity(options);
  }
}
